import { MessageDescriptor } from '@redbubble/next/intl';
import {
  Item as AnalyticsItem,
  AnalyticsPageTypeProperty,
} from '@redbubble/next-analytics/gtm';
import type {
  StripeElementType,
  StripeError,
  ExpressPaymentType as StripeExpressPaymentType,
} from '@stripe/stripe-js';
import { ObjectValues } from '../../types';

export interface PayError {
  code: string;
  message?: string;
  title?: string;
}

export type ErrorMessage = MessageDescriptor | string;

export const RbChargeSources = {
  Stripe: 'STRIPE',
  AfterPayClearPay: 'afterpay',
  ApplePay: 'APPLE_PAY',
  GooglePay: 'GOOGLE_PAY',
  Klarna: 'klarna',
} as const;

export const StripePaymentMethods = {
  Card: 'card',
  Klarna: 'klarna',
  AfterPayClearPay: 'afterpay_clearpay',
  GooglePay: 'google_pay',
  ApplePay: 'apple_pay',
  Paypal: 'external_paypal',
} as const;

export const RbPaymentMethods = {
  Card: 'card',
  Klarna: 'klarna',
  Sofort: 'sofort',
  AfterPayClearPay: 'afterpay_clearpay',
  Paypal: 'paypal',
} as const;

export const PageSource = {
  cart: 'cart',
  checkout: 'check-out',
  cartTray: 'cart-tray',
} as const;

export type StripePaymentMethodType = ObjectValues<typeof StripePaymentMethods>;

export type LoadErrorEvent<T = StripeElementType> = {
  elementType: T;
  error: StripeError;
};

export type OnLoadError<T = StripeElementType> = (
  event: LoadErrorEvent<T>
) => void;

export type InternalStripePaymentMethodType = Exclude<
  StripePaymentMethodType,
  'external_paypal'
>;

export type RBChargeSourceType = ObjectValues<typeof RbChargeSources>;

export type RbPaymentMethodType = ObjectValues<typeof RbPaymentMethods>;

export type PageSourceType = ObjectValues<typeof PageSource>;

export type OnCancel<T = StripeElementType> = (event: {
  elementType: T;
}) => void;

type EstimatedShipping = {
  express: number | null;
  expressExTax: number | null;
  preference: ShippingMethod;
  standard: number;
  standardExTax: number;
};

type Summary = {
  currency: string;
  estimatedShipping: EstimatedShipping;
  lineItemsCount: number;
  showTaxSeparately: boolean;
  subtotal: number;
  tax: Tax | null;
  total: number;
};

type CouponType =
  | 'AmountCoupon'
  | 'FreeSampleCoupon'
  | 'FreeShippingCoupon'
  | 'GiftCertificateCoupon'
  | 'PercentOffCoupon'
  | 'VariablePercentOffCoupon';

type AppliedCoupon = {
  code: string;
  discount: number;
  displayName: string;
  type: CouponType;
};

type Tax = {
  amount: number;
  type: string;
};

type ShippingMethod = 'STANDARD' | 'EXPRESS';

export interface StripeCart {
  appliedCoupon: AppliedCoupon | null;
  summary: Summary;
}

export type StripeExpressAnalyticsPageType = Extract<
  AnalyticsPageTypeProperty,
  'cart' | 'pdp'
>;

export type ExpressPaymentType = Extract<
  StripeExpressPaymentType,
  'apple_pay' | 'google_pay'
>;

export const ExpressPayment = {
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
} as const satisfies Record<string, ExpressPaymentType>;

export type StripeAnalyticsItems = (AnalyticsItem & { coupon: string })[];
